import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import {
  fetchAdminCatalogue,
  adminUpdateProduct,
  adminSetProductVisible,
  adminDeleteProduct,
  isAdminUser,
} from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Loader2,
  Search,
  Package,
  FileDown,
  Check,
  Plus,
  Gift,
  Copy,
  Trash2,
  X,
} from "lucide-react";
import CatalogueEditSheet from "@/components/CatalogueEditSheet";
import NewProductPanel from "@/components/NewProductPanel";
import {
  PromoDialog,
  BundleDialog,
  DuplicateDialog,
} from "@/components/CatalogueDialogs";
import ShippingTab from "@/components/ShippingTab";
import SeoTab from "@/components/SeoTab";
import ChatGptCommerceTab from "@/components/acp/ChatGptCommerceTab";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

/**
 * Admin Product Catalogue.
 *
 * Lists every product in the OreVital Airtable Products table (active +
 * inactive) with inline Active toggle, Search, "Active Only" filter and
 * a CSV export.  Clicking a row opens the CatalogueEditSheet where the
 * admin can edit MSRP / weight / inventory / rank, AND maintain the
 * per-role pricing matrix (one row per role in the Pricing table).
 *
 * Admin-only — bounces non-admins to home.
 */
export default function Catalogue() {
  const { user } = useAuth();
  const callerRecId = user?.record_id;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeOnly, setActiveOnly] = useState(true);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [promoFor, setPromoFor] = useState(null);
  const [duplicateFor, setDuplicateFor] = useState(null);
  const [bundleOpen, setBundleOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [tab, setTab] = useState("products"); // 'products' | 'shipping' | 'seo'
  const isAdmin = isAdminUser(user);

  const load = useCallback(async () => {
    if (!callerRecId) return;
    setLoading(true);
    try {
      const res = await fetchAdminCatalogue({
        callerUserRecordId: callerRecId,
        includeInactive: 1,
      });
      setItems(res?.products || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [callerRecId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((p) => {
      if (activeOnly && !p.active) return false;
      if (ql && !p.name.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [items, activeOnly, q]);

  const totalActive = useMemo(
    () => items.filter((p) => p.active).length,
    [items]
  );

  async function toggleActive(p) {
    const next = p.active ? 0 : 1;
    // optimistic
    setItems((cur) =>
      cur.map((x) => (x.id === p.id ? { ...x, active: next } : x))
    );
    try {
      await adminUpdateProduct({
        callerUserRecordId: callerRecId,
        productRecordId: p.id,
        fields: { active: next },
      });
      toast.success(`${p.name} ${next ? "activated" : "deactivated"}`);
    } catch (e) {
      // rollback
      setItems((cur) =>
        cur.map((x) => (x.id === p.id ? { ...x, active: p.active } : x))
      );
      toast.error(e?.response?.data?.detail || "Could not toggle active");
    }
  }

  async function toggleHomePage(p) {
    const next = !p.visible;
    // optimistic
    setItems((cur) =>
      cur.map((x) => (x.id === p.id ? { ...x, visible: next } : x))
    );
    try {
      await adminSetProductVisible({
        callerUserRecordId: callerRecId,
        productRecordId: p.id,
        visible: next,
      });
      toast.success(
        `${p.name} ${next ? "now visible" : "hidden"} on mindfulminerals.com`
      );
    } catch (e) {
      setItems((cur) =>
        cur.map((x) => (x.id === p.id ? { ...x, visible: p.visible } : x))
      );
      toast.error(e?.response?.data?.detail || "Could not toggle Visible");
    }
  }

  function exportCsv() {
    const rows = filtered;
    const header = [
      "Product Name",
      "MSRP",
      "Weight",
      "Count",
      "Rank",
      "Active",
      "Slug",
      "Post ID",
    ];
    const csv = [header.join(",")];
    for (const p of rows) {
      const cells = [
        `"${(p.name || "").replace(/"/g, '""')}"`,
        p.price,
        p.weight,
        p.inventory,
        p.rank,
        p.active ? "Yes" : "No",
        p.slug,
        p.post_id,
      ];
      csv.push(cells.join(","));
    }
    const blob = new Blob([csv.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `catalogue-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function toggleOne(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleDelete(p) {
    if (!window.confirm(`Soft-delete "${p.name}"? (sets active=0, can be re-enabled later)`)) {
      return;
    }
    try {
      await adminDeleteProduct({
        callerUserRecordId: callerRecId,
        productRecordId: p.id,
      });
      setItems((cur) => cur.map((x) => (x.id === p.id ? { ...x, active: 0 } : x)));
      toast.success(`${p.name} deactivated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  const selectedProducts = items.filter((p) => selected.has(p.id));

  if (!isAdmin && callerRecId) {
    return (
      <div className="px-4 py-10 text-center text-sm text-zinc-500">
        Admin access required.
      </div>
    );
  }

  return (
    <div className="px-4 pt-3 pb-32" data-testid="catalogue-page">
      {/* Single compact header row: tabs + (products-only) actions */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex rounded-xl bg-white border border-zinc-200 p-0.5">
          {[
            { k: "products", l: "Products" },
            { k: "shipping", l: "Shipping" },
            { k: "seo", l: "SEO" },
            { k: "chatgpt", l: "ChatGPT" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`h-8 px-3 rounded-lg text-xs font-semibold transition ${
                tab === t.k
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-700 hover:bg-zinc-50"
              }`}
              data-testid={`catalogue-tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <>
            <button
              onClick={() => setNewOpen((v) => !v)}
              className="h-8 px-3 rounded-xl text-xs font-semibold bg-violet-600 text-white inline-flex items-center gap-1.5 hover:bg-violet-700"
              data-testid="catalogue-new-product"
            >
              <Plus size={13} />
              New product
            </button>
            <button
              onClick={() => setActiveOnly((v) => !v)}
              className={`h-8 px-3 rounded-xl text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                activeOnly
                  ? "bg-zinc-900 text-white"
                  : "bg-white border border-zinc-200 text-zinc-700"
              }`}
              data-testid="catalogue-active-only"
            >
              <Check size={13} />
              Active Only
            </button>
            <button
              onClick={exportCsv}
              title="Export CSV"
              aria-label="Export CSV"
              className="h-8 w-8 rounded-xl bg-white border border-zinc-200 text-zinc-700 inline-flex items-center justify-center hover:bg-zinc-50"
              data-testid="catalogue-export-csv"
            >
              <FileDown size={14} />
            </button>
          </>
        )}
      </div>

      {tab === "shipping" && (
        <ShippingTab callerUserRecordId={callerRecId} />
      )}
      {tab === "seo" && <SeoTab callerUserRecordId={callerRecId} />}
      {tab === "chatgpt" && <ChatGptCommerceTab callerUserRecordId={callerRecId} />}
      {tab === "products" && (
        <>
      {newOpen && (
        <NewProductPanel
          callerUserRecordId={callerRecId}
          products={items}
          onClose={() => setNewOpen(false)}
          onCreated={(res) => {
            setNewOpen(false);
            load();
            if (res?.id) setEditingId(res.id);
          }}
        />
      )}

      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            placeholder="Search products…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-xl bg-white"
            data-testid="catalogue-search"
          />
        </div>
      </div>

      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-sm font-bold text-violet-700">
          Products ({filtered.length})
        </span>
        <span className="text-xs text-zinc-500">
          {totalActive} active · {items.length} total
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin text-zinc-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-sm text-zinc-500 py-10">
          No products match.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block rounded-2xl border border-zinc-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                <tr>
                  <th className="w-10"></th>
                  <th className="text-left px-3 py-2 font-semibold">
                    Product Name
                  </th>
                  <th className="text-right px-3 py-2 font-semibold">MSRP</th>
                  <th className="text-right px-3 py-2 font-semibold">
                    Weight
                  </th>
                  <th className="text-right px-3 py-2 font-semibold">Count</th>
                  <th className="text-center px-3 py-2 font-semibold">
                    Active
                  </th>
                  <th
                    className="text-center px-3 py-2 font-semibold"
                    title="Show on mindfulminerals.com — writes to Pricing.Visible for every role"
                  >
                    Visible
                  </th>
                  <th className="text-right px-3 py-2 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    className="border-t border-zinc-100 hover:bg-zinc-50/60"
                    data-testid={`catalogue-row-${p.id}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={selected.has(p.id)}
                        onChange={() => toggleOne(p.id)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setEditingId(p.id)}
                        className="flex items-center gap-2.5 group text-left"
                        data-testid={`catalogue-edit-${p.id}`}
                      >
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt=""
                            className="w-9 h-9 rounded-lg object-cover bg-zinc-100 shrink-0"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0">
                            <Package size={14} />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold text-zinc-900 group-hover:text-violet-700 leading-tight">
                            {p.name}
                          </div>
                          {p.promo && (
                            <span
                              className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${
                                p.promo.active
                                  ? "bg-rose-100 text-rose-700"
                                  : "bg-zinc-100 text-zinc-500"
                              }`}
                              title={
                                p.promo.active
                                  ? "Active BOGO promo"
                                  : "Scheduled / expired promo"
                              }
                              data-testid={`catalogue-promo-badge-${p.id}`}
                            >
                              <Gift size={10} />
                              {p.promo.label}
                              {!p.promo.active && " (off)"}
                            </span>
                          )}
                        </div>
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {fmtUSD(p.price)}
                    </td>
                    <td className="px-3 py-2 text-right text-zinc-600 tabular-nums">
                      {p.weight}
                    </td>
                    <td className="px-3 py-2 text-right text-zinc-600 tabular-nums">
                      {p.inventory}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Switch
                        checked={!!p.active}
                        onCheckedChange={() => toggleActive(p)}
                        data-testid={`catalogue-active-${p.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Switch
                        checked={!!p.visible}
                        onCheckedChange={() => toggleHomePage(p)}
                        data-testid={`catalogue-visible-${p.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setPromoFor(p)}
                          className={`w-7 h-7 rounded-md inline-flex items-center justify-center ${
                            p.promo
                              ? "bg-rose-50 text-rose-600 ring-1 ring-rose-200"
                              : "text-rose-600 hover:bg-rose-50"
                          }`}
                          title={
                            p.promo
                              ? `Edit promo: ${p.promo.label}`
                              : "Add Buy X Get Y Free promo"
                          }
                          data-testid={`catalogue-promo-${p.id}`}
                        >
                          <Gift size={14} />
                        </button>
                        <button
                          onClick={() => setDuplicateFor(p)}
                          className="w-7 h-7 rounded-md text-emerald-600 hover:bg-emerald-50 inline-flex items-center justify-center"
                          title="Duplicate product"
                          data-testid={`catalogue-duplicate-${p.id}`}
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(p)}
                          className="w-7 h-7 rounded-md text-zinc-500 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center"
                          title="Soft-delete (set inactive)"
                          data-testid={`catalogue-delete-${p.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile list */}
          <ul className="sm:hidden space-y-2" data-testid="catalogue-mobile-list">
            {filtered.map((p) => (
              <li
                key={p.id}
                className="rounded-2xl border border-zinc-200 bg-white p-3 flex items-center gap-3"
              >
                <button
                  onClick={() => setEditingId(p.id)}
                  className="flex-1 flex items-center gap-2.5 min-w-0 text-left"
                >
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt=""
                      className="w-11 h-11 rounded-lg object-cover bg-zinc-100 shrink-0"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0">
                      <Package size={16} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-zinc-900 truncate">
                      {p.name}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {fmtUSD(p.price)} · {p.inventory} in stock
                    </div>
                    {p.promo && (
                      <span
                        className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${
                          p.promo.active
                            ? "bg-rose-100 text-rose-700"
                            : "bg-zinc-100 text-zinc-500"
                        }`}
                      >
                        <Gift size={10} />
                        {p.promo.label}
                      </span>
                    )}
                  </div>
                </button>
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] uppercase tracking-wide text-zinc-400">
                      Active
                    </span>
                    <Switch
                      checked={!!p.active}
                      onCheckedChange={() => toggleActive(p)}
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] uppercase tracking-wide text-zinc-400">
                      Visible
                    </span>
                    <Switch
                      checked={!!p.visible}
                      onCheckedChange={() => toggleHomePage(p)}
                    />
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => setPromoFor(p)}
                      className="w-7 h-7 rounded-md text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center"
                      title="Promo"
                    >
                      <Gift size={13} />
                    </button>
                    <button
                      onClick={() => setDuplicateFor(p)}
                      className="w-7 h-7 rounded-md text-emerald-600 hover:bg-emerald-50 inline-flex items-center justify-center"
                      title="Duplicate"
                    >
                      <Copy size={13} />
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      className="w-7 h-7 rounded-md text-zinc-500 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
        </>
      )}

      <CatalogueEditSheet
        open={!!editingId}
        onOpenChange={(o) => !o && setEditingId(null)}
        productRecordId={editingId}
        callerUserRecordId={callerRecId}
        onSaved={() => load()}
      />

      <PromoDialog
        open={!!promoFor}
        onOpenChange={(o) => !o && setPromoFor(null)}
        product={promoFor}
        callerUserRecordId={callerRecId}
        onSaved={() => load()}
      />

      <DuplicateDialog
        open={!!duplicateFor}
        onOpenChange={(o) => !o && setDuplicateFor(null)}
        product={duplicateFor}
        callerUserRecordId={callerRecId}
        onSaved={() => load()}
      />

      <BundleDialog
        open={bundleOpen}
        onOpenChange={setBundleOpen}
        products={selectedProducts}
        callerUserRecordId={callerRecId}
        onSaved={() => {
          setBundleOpen(false);
          setSelected(new Set());
          load();
        }}
      />

      {/* Floating selection bar — appears when 2+ products are checked */}
      {selectedProducts.length >= 2 && (
        <div
          className="fixed bottom-16 sm:bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-2xl bg-zinc-900 text-white shadow-2xl flex items-center gap-2 px-3 py-2"
          data-testid="catalogue-selection-bar"
        >
          <span className="text-sm font-semibold pl-1">
            {selectedProducts.length} selected
          </span>
          <button
            onClick={() => setBundleOpen(true)}
            className="h-9 px-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-xs font-semibold inline-flex items-center gap-1.5"
            data-testid="catalogue-create-bundle"
          >
            <Package size={13} />
            Create Bundle
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="w-8 h-8 rounded-xl bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center"
            title="Clear selection"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
